import { Action, createReducer, on } from '@ngrx/store';
import {
  loginSuccess,
  logout,
  mfaSetupRequired,
  mfaVerificationRequired,
  tokenRefreshSuccess,
  userPermissions,
  userProfile,
  userSupplierV1,
} from '../actions/authentication.actions';
import { JwtHelperService } from '@auth0/angular-jwt';
import { FeaturePermissionDto } from '../../../api/models/feature-permission-dto';
import { UserQueryDto } from '../../../api/models/user-query-dto';
import { SupplierListV1 } from 'src/app/api/models';

export interface AuthState {
  userName: string;
  userRole: string;
  userToken: string;
  userPermissions: FeaturePermissionDto[];
  userSupplierV1: SupplierListV1;
  userProfile: UserQueryDto;
  loggedIn: boolean;
  mfaSetupRequired: boolean;
  mfaVerificationRequired: boolean;
}

export const initialState: AuthState = {
  userName: '',
  userRole: '',
  userToken: '',
  userPermissions: [],
  userSupplierV1: {},
  userProfile: {},
  loggedIn: false,
  mfaSetupRequired: false,
  mfaVerificationRequired: false,
};

const authReducer = createReducer(
  initialState,
  on(loginSuccess, (state, result) => setUserDetails(state, result)),
  on(userPermissions, (state, result) => setUserPermissions(state, result)),
  on(userSupplierV1, (state, result) => setUserSupplierV1(state, result)),
  on(userProfile, (state, result) => setUserProfile(state, result)),
  on(tokenRefreshSuccess, (state, result) => setUserDetails(state, result)),
  on(mfaSetupRequired, (state) => ({ ...state, mfaSetupRequired: true, mfaVerificationRequired: false })),
  on(mfaVerificationRequired, (state) => ({ ...state, mfaVerificationRequired: true, mfaSetupRequired: false })),
  on(logout, () => reset())
);

export const setUserDetails = (state: AuthState, result: any) => {
  const decodedToken = new JwtHelperService().decodeToken(result.token);

  return {
    ...state,
    userToken: result.token.toString(),
    userRole: decodedToken.role,
    userName: decodedToken.sub,
    loggedIn: true,
    mfaSetupRequired: false,
    mfaVerificationRequired: false,
  };
};

export const setUserSupplierV1 = (state: AuthState, result: any) => {
  return {
    ...state,
    userSupplierV1: result.supplier,
  };
};

export const setUserPermissions = (state: AuthState, result: any) => {
  return {
    ...state,
    userPermissions: result.userPermissions,
  };
};

export const setUserProfile = (state: AuthState, result: any) => {
  const updatedRole = result.userProfile?.accessControl;
  return {
    ...state,
    userRole: updatedRole ?? state.userRole,
    userProfile: {
      ...state.userProfile,
      ...result.userProfile,
    },
  };
};

export const reset = () => initialState;
export const getUserName = (state: AuthState) => state.userName;
export const getUserRole = (state: AuthState) => state.userRole;
export const getUserToken = (state: AuthState) => state.userToken;
export const getUserPermissions = (state: AuthState) => state.userPermissions;
export const getUserSupplierV1 = (state: AuthState) => state.userSupplierV1;
export const getUserProfile = (state: AuthState) => state.userProfile;
export const isLoggedIn = (state: AuthState) => state.loggedIn;
export const getMfaSetupRequired = (state: AuthState) => state.mfaSetupRequired;
export const getMfaVerificationRequired = (state: AuthState) => state.mfaVerificationRequired;

export function reducer(state: AuthState | undefined, action: Action): any {
  return authReducer(state, action);
}
